import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { adminSupabase } from '@/integrations/supabase/adminClient';
import { shouldClearPortalStateForAuthSessionChange } from '@/lib/portalAuthSession';
import {
  resolveCurrentPortalIdentity,
  type PortalIdentityResolverResult,
} from '@/lib/portalIdentityResolver';

type PortalIdentityResolverClient = Parameters<typeof resolveCurrentPortalIdentity>[0];

interface AdminAuthContextType {
  session: Session | null;
  user: User | null;
  profile: { full_name: string; email: string; user_type: string } | null;
  isPlatformAdmin: boolean;
  loading: boolean;
  portalIdentityDiagnostic: PortalIdentityResolverResult | null;
  portalIdentityLoading: boolean;
  refreshPortalIdentityDiagnostic: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType>({
  session: null,
  user: null,
  profile: null,
  isPlatformAdmin: false,
  loading: true,
  portalIdentityDiagnostic: null,
  portalIdentityLoading: false,
  refreshPortalIdentityDiagnostic: async () => {},
  signOut: async () => {},
});

export const useAdminAuth = () => useContext(AdminAuthContext);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AdminAuthContextType['profile']>(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [portalIdentityDiagnostic, setPortalIdentityDiagnostic] =
    useState<PortalIdentityResolverResult | null>(null);
  const [portalIdentityLoading, setPortalIdentityLoading] = useState(false);
  const portalIdentityRequestSeqRef = useRef(0);
  const portalIdentityInFlightUserRef = useRef<string | null>(null);
  const activeAdminUserIdRef = useRef<string | null>(null);

  const clearPortalIdentityDiagnostic = useCallback(() => {
    portalIdentityRequestSeqRef.current += 1;
    portalIdentityInFlightUserRef.current = null;
    setPortalIdentityDiagnostic(null);
    setPortalIdentityLoading(false);
  }, []);

  const runPortalIdentityDiagnostic = useCallback(
    async (userId: string, options: { force?: boolean } = {}) => {
      if (
        !options.force
        && portalIdentityInFlightUserRef.current === userId
      ) {
        return;
      }

      const requestSeq = portalIdentityRequestSeqRef.current + 1;
      portalIdentityRequestSeqRef.current = requestSeq;
      portalIdentityInFlightUserRef.current = userId;
      setPortalIdentityLoading(true);

      try {
        const result = await resolveCurrentPortalIdentity(
          adminSupabase as unknown as PortalIdentityResolverClient,
          'admin',
        );

        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic(result);
        }
      } catch {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic({
            ok: false,
            ready: false,
            reason: 'resolver_error',
            portal_auth_identity_id: null,
            portal: null,
            user_type: null,
            auth_provider: null,
            auth_instance_id: null,
            auth_user_id: null,
            status: null,
            auth_identity_kind: null,
          });
        }
      } finally {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          portalIdentityInFlightUserRef.current = null;
          setPortalIdentityLoading(false);
        }
      }
    },
    [],
  );

  const fetchProfileAndRole = useCallback(async (userId: string) => {
    const [profileRes, adminRes] = await Promise.all([
      adminSupabase
        .from('admin_profiles')
        .select('full_name, email')
        .eq('user_id', userId)
        .single(),
      adminSupabase.rpc('is_platform_admin', { _user_id: userId }),
    ]);

    if (activeAdminUserIdRef.current !== userId) {
      return;
    }

    if (!profileRes.error && profileRes.data) {
      setProfile({ ...profileRes.data, user_type: 'platform_admin' });
    }

    const nextIsPlatformAdmin = !!adminRes.data;
    setIsPlatformAdmin(nextIsPlatformAdmin);

    if (nextIsPlatformAdmin) {
      void runPortalIdentityDiagnostic(userId);
    } else {
      clearPortalIdentityDiagnostic();
    }
  }, [clearPortalIdentityDiagnostic, runPortalIdentityDiagnostic]);

  const refreshPortalIdentityDiagnostic = useCallback(async () => {
    if (!user?.id || !profile || !isPlatformAdmin) {
      clearPortalIdentityDiagnostic();
      return;
    }

    await runPortalIdentityDiagnostic(user.id, { force: true });
  }, [clearPortalIdentityDiagnostic, isPlatformAdmin, profile, runPortalIdentityDiagnostic, user?.id]);

  const applyAdminSession = useCallback(async (nextSession: Session | null) => {
    const previousUserId = activeAdminUserIdRef.current;
    const nextUserId = nextSession?.user?.id ?? null;
    const shouldClearState = shouldClearPortalStateForAuthSessionChange(previousUserId, nextUserId);
    activeAdminUserIdRef.current = nextUserId;
    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    if (!nextSession?.user) {
      setProfile(null);
      setIsPlatformAdmin(false);
      clearPortalIdentityDiagnostic();
      setLoading(false);
      return;
    }

    if (shouldClearState) {
      setLoading(true);
      setProfile(null);
      setIsPlatformAdmin(false);
      clearPortalIdentityDiagnostic();
    }

    try {
      await fetchProfileAndRole(nextSession.user.id);
    } finally {
      if (activeAdminUserIdRef.current === nextSession.user.id && shouldClearState) {
        setLoading(false);
      }
    }
  }, [clearPortalIdentityDiagnostic, fetchProfileAndRole]);

  useEffect(() => {
    const { data: { subscription } } = adminSupabase.auth.onAuthStateChange(
      async (_event, session) => {
        void applyAdminSession(session);
      }
    );

    adminSupabase.auth.getSession().then(({ data: { session } }) => {
      void applyAdminSession(session);
    });

    return () => subscription.unsubscribe();
  }, [applyAdminSession]);

  const signOut = async () => {
    await adminSupabase.auth.signOut();
    activeAdminUserIdRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
    setIsPlatformAdmin(false);
    clearPortalIdentityDiagnostic();
  };

  return (
    <AdminAuthContext.Provider
      value={{
        session,
        user,
        profile,
        isPlatformAdmin,
        loading,
        portalIdentityDiagnostic,
        portalIdentityLoading,
        refreshPortalIdentityDiagnostic,
        signOut,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}
